import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api, { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'
import { generateSampleExcel, parseProjectExcel } from '../../utils/excelTemplate'

// --- Internal Step Components ---
import Step1BasicInfo from './project-steps/Step1BasicInfo'
import Step2Location from './project-steps/Step2Location'
import Step3Developer from './project-steps/Step3Developer'
import Step4Overview from './project-steps/Step4Overview'
import Step5SEO from './project-steps/Step5SEO'
import Step4FloorPlan from './project-steps/Step4FloorPlan'
import Step5Inventory from './project-steps/Step5Inventory'
import Step6Pricing from './project-steps/Step5Pricing'
import Step7Content from './project-steps/Step6Content'
import Step8Media from './project-steps/Step7Media'
import Step8Grid from './project-steps/Step8Grid'
import Step9Offers from './project-steps/Step9Offers'
import Step13SectionInfo from './project-steps/Step12SectionInfo'

// --- Types & Constants ---
import { 
    ProjectForm, 
    FloorPlan, 
    AdditionalDetail, 
    Specification, 
    LegalDocument, 
    FAQ, 
    PriceBreakup, 
    NearbyPlace, 
    PaymentPlan, 
    InventoryItem,
    GalleryCategory,
    GalleryState,
    GalleryFileState
} from './project-steps/types'
import { 
    SUBCATEGORIES, 
    STEPS, 
    NEARBY_CATEGORY_ICONS, 
    DEFAULT_CONSTRUCTION_STATUS_OPTIONS, 
    BANK_OPTIONS 
} from './project-steps/constants'

export default function AdminAddProject() {
    const { token } = useAuth()
    const navigate = useNavigate()
    const [currentStep, setCurrentStep] = useState(1)
    const [activeStep4Tab, setActiveStep4Tab] = useState<'2d' | '3d' | null>('2d')
    const [submitting, setSubmitting] = useState(false)
    const [submittingPkgId, setSubmittingPkgId] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [errors, setErrors] = useState<Record<string, boolean>>({})
    const [activeProjectId, setActiveProjectId] = useState<string | null>(null)
    const [userProfile, setUserProfile] = useState<any>(null);
    const [availablePackages, setAvailablePackages] = useState<any[]>([]);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [showNearbyModal, setShowNearbyModal] = useState(false);
    const [nearbyEntry, setNearbyEntry] = useState<NearbyPlace>({ category: 'Schools', name: '', distance: '' });

    // Media Folder Loader State
    const [searchParams] = useSearchParams();
    const [mediaFolderInput, setMediaFolderInput] = useState<string>('');
    const [appliedMediaFolder, setAppliedMediaFolder] = useState<any>(null);
    const [loadingMediaFolder, setLoadingMediaFolder] = useState(false);
    const [availableMediaFolders, setAvailableMediaFolders] = useState<any[]>([]);

    // Form Master State
    const [form, setForm] = useState<ProjectForm>({
        name: '', listingType: 'For Sale', propertyType: ['Residential'], subCategory: ['Flat/Apartment'], projectStatus: 'Under Construction', progressPercentage: 0, tagline: '', slug: '', isOurPick: false,
        location: '', address: '', city: '', state: 'Gujarat', country: 'India', pinCode: '', googleMapLocation: '', google3dViewLink: '', latitude: '', longitude: '',
        developerName: '', coDeveloper: '', developerLogo: '', developerWebsite: '', developerDescription: '', developerTagline: '', totalProjects: '0', projectsCompleted: '0', projectsOngoing: '0',
        projectsUpcoming: '0',
        projectsSince: '',
        aboutDeveloper: '',
        developerId: '',
        // Step 4
        overviewConfiguration: '', overviewSizeRange: '', overviewPriceRange: '', overviewAvgPricePerSqFt: '', overviewProjectArea: '', overviewTotalPlotArea: '', overviewOpenSpacePercentage: '', overviewTotalTowers: '', overviewTotalFloors: '', overviewTotalUnits: '', overviewFloors: '',
        overviewAmenitiesIcon: '',
        
        // Step 5
        towers: [],
        
        // Step 6
        launchDate: '', possessionDate: '', timelineStatus: '', constructionStatusScore: 0,
        reraApproved: 'Yes', reraNumber: '', reraAuthority: 'Gujarat Real Estate Regulatory Authority', reraLink: '',
        shortDescription: '', fullDescription: '', 
        highlights: [
            { icon: 'fa-location-dot', text: 'Prime Location' },
            { icon: 'fa-city', text: 'Premium Residences' },
            { icon: 'fa-leaf', text: '65% Open Space' }
        ], 
        investmentPotential: '', videoUrl: '',
        brochure: null,
        brochureUrl: '',
        images: [],
        virtualTours: [{ title: '', link: '' }],
        youtubeVideos: [{ title: '', link: '' }],
        builderOffers: [{ offerType: '', example: '', priceImpact: '', startDate: '', tillDate: '' }],
        faq: [{ question: '', answer: '' }],
        metaTitle: '', metaDescription: '', metaKeywords: '', canonicalUrl: '', focusKeyword: '',
        contactNumber: '', contactPhone: '', whatsappNumber: '', contactEmail: '',
        disclaimer: '',

        paymentPlans: [{ title: 'Possession Linked', description: 'Pay 25% now, rest on possession' }],
        bankApprovals: [],
        tpImage: '',
        tpImageUrl: '',
        builderTrustScore: 0,
        localityTrustScore: 0,
        loadingScore: 0,
        legalTrustScore: 0,
        amenitiesScore: 0,
        specificationsScore: 0,
        status: 'Pending',
        nearbyPlaces: [],
        dynamicSlots: [],
        amenityIcons: {},
        customAmenities: [],
        sectionInfo: {
            overview: '',
            amenities: '',
            specifications: '',
            location: '',
            floorPlans: '',
            gallery: '',
            developer: '',
            nearby: '',
            about: '',
            highlights: '',
            faq: '',
            reviews: ''
        },
    })

    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false)

    // Complex Dynamic Fields
    const [additionalDetails, setAdditionalDetails] = useState<AdditionalDetail[]>([{ label: '', value: '', icon: '' }])
    const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([{
        unitType: '',
        subUnits: [{ subUnitType: '', subUnitVariant: '', price: '', facing: [], reraArea: '', carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '', infoImage: '', infoImagePreview: '' }]
    }])
    const [inventory, setInventory] = useState<InventoryItem[]>([{ tower: '', floor: '', unitNo: '', unitType: '', configuration: '', size: '', price: '', status: 'Available' }])
    const [specifications, setSpecifications] = useState<Specification[]>([])
    const [selectedAmenities, setSelectedAmenities] = useState<string[]>([])
    const [globalAmenities, setGlobalAmenities] = useState<{ _id: string, name: string, icon: string, score: number }[]>([])
    const [globalSpecs, setGlobalSpecs] = useState<{ _id: string, name: string, icon: string, details: string }[]>([])
    const [globalDetails, setGlobalDetails] = useState<{ _id: string, label: string, icon: string }[]>([])
    const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([{ title: '', image: '', imagePreview: '' }])
    const [faqs, setFaqs] = useState<FAQ[]>([{ question: '', answer: '' }])
    const [developers, setDevelopers] = useState<{
        _id: string, name: string, logo?: string, website?: string, totalProjects?: string, projectsCompleted?: string, projectsOngoing?: string, projectsUpcoming?: string;
        projectsSince?: string;
        deliveryRecord?: string;
    }[]>([]);
    const [cities, setCities] = useState<{ _id: string, name: string }[]>([])
    const [allLocalities, setAllLocalities] = useState<{ _id: string, name: string, city: any }[]>([])
    const [filteredLocalities, setFilteredLocalities] = useState<{ _id: string, name: string }[]>([])
    const [imagePreviews, setImagePreviews] = useState<string[]>([])
    const [galleryPreviews, setGalleryPreviews] = useState<GalleryState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [galleryIcons, setGalleryIcons] = useState<Record<string, string>>({})
    const [globalLandmarkCategories, setGlobalLandmarkCategories] = useState<{ _id: string, name: string, icon: string }[]>([])
    const [scoreSettings, setScoreSettings] = useState<any>(null);

    // Media State
    const [featuredImage, setFeaturedImage] = useState<File | null>(null)
    const [featuredPreview, setFeaturedPreview] = useState('')
    const [tpImage, setTpImage] = useState<File | null>(null)
    const [tpPreview, setTpPreview] = useState('')
    const [seoImageFile, setSeoImageFile] = useState<File | null>(null)
    const [seoImagePreview, setSeoImagePreview] = useState('')
    const [gallery, setGallery] = useState<GalleryFileState>({
        exterior: [], interior: [], amenities: [], construction: []
    })
    const [brochureFile, setBrochureFile] = useState<File | null>(null)
    const [projectVideoFile, setProjectVideoFile] = useState<File | null>(null)

    const updateGalleryIcon = async (album: string, icon: File | string) => {
        if (typeof icon === 'string') {
            setGalleryIcons(prev => ({ ...prev, [album]: icon }));
        } else {
            try {
                const urls = await handleUpload([icon]);
                if (urls[0]) {
                    setGalleryIcons(prev => ({ ...prev, [album]: urls[0] }));
                }

                if (scoreSettings && !scoreSettings.message) {
                    setScoreSettings(scoreSettings);
                }
            } catch (err) {

                toast.error("Failed to upload icon");
            }
        }
    };

    const removeGalleryCategory = (album: string) => {
        const newGallery = { ...gallery };
        delete newGallery[album];
        setGallery(newGallery);

        const newPreviews = { ...galleryPreviews };
        delete newPreviews[album];
        setGalleryPreviews(newPreviews);

        const newIcons = { ...galleryIcons };
        delete newIcons[album];
        setGalleryIcons(newIcons);
    };
    const [constructionStatusImage, setConstructionStatusImage] = useState<File | null>(null)
    const [constructionStatusPreview, setConstructionStatusPreview] = useState('')
    const [importing, setImporting] = useState(false)
    const [showImportConfirm, setShowImportConfirm] = useState(false)
    const [pendingImportData, setPendingImportData] = useState<any[]>([])
    const fileInputRef = React.useRef<HTMLInputElement>(null)

    const handleGalleryUpload = (category: string, files: File[]) => {
        setGallery(prev => ({ ...prev, [category]: [...(prev[category] || []), ...files] }));
        const newPreviews = files.map(f => URL.createObjectURL(f));
        setGalleryPreviews(prev => ({ ...prev, [category]: [...(prev[category] || []), ...newPreviews] }));
    };

    const removeGalleryImage = (category: string, idx: number) => {
        setGallery(prev => {
            const next = { ...prev };
            if (next[category]) {
                next[category] = next[category].filter((_: File, i: number) => i !== idx);
            }
            return next;
        });
        setGalleryPreviews(prev => {
            const next = { ...prev };
            if (next[category]) {
                next[category] = next[category].filter((_: string, i: number) => i !== idx);
            }
            return next;
        });
    };

    useEffect(() => {
        if (token) {
            loadProfile();
            // Fetch available media folders for quick select
            api.getMediaFolders(token || undefined).then((folders: any) => {
                if (Array.isArray(folders)) {
                    setAvailableMediaFolders(folders);
                }
            }).catch(console.error);

            // Check if URL specifies a media folder
            const paramFolder = searchParams.get('mediaFolder');
            if (paramFolder) {
                setMediaFolderInput(paramFolder);
                handleApplyMediaFolder(paramFolder);
            }
        }
    }, [token]);

    const handleApplyMediaFolder = async (folderNumStr?: string | number) => {
        const num = parseInt(String(folderNumStr || mediaFolderInput));
        if (isNaN(num) || num <= 0) {
            toast.error("Please enter a valid Folder Number (e.g. 1)");
            return;
        }
        setLoadingMediaFolder(true);
        try {
            const folder = await api.getMediaFolder(num, token || undefined);
            if (!folder || !folder.folderNumber) {
                toast.error(`Media Folder #${num} not found.`);
                return;
            }

            setAppliedMediaFolder(folder);
            setForm(prev => ({
                ...prev,
                mediaFolderNumber: folder.folderNumber,
                featuredImage: folder.coverImage || prev.featuredImage,
                coverImage: folder.coverImage || prev.coverImage,
                brochureUrl: folder.documents?.brochure ? getImageUrl(folder.documents.brochure) : prev.brochureUrl,
                tpImageUrl: folder.documents?.tpImage ? getImageUrl(folder.documents.tpImage) : prev.tpImageUrl,
                images: [
                    ...(folder.coverImage ? [folder.coverImage] : []),
                    ...Object.values(folder.gallery || {}).flat()
                ]
            }));

            if (folder.coverImage) {
                setFeaturedPreview(getImageUrl(folder.coverImage));
            } else if (folder.gallery?.topPhotos?.length) {
                setFeaturedPreview(getImageUrl(folder.gallery.topPhotos[0]));
            }

            if (folder.documents?.tpImage) {
                setTpPreview(getImageUrl(folder.documents.tpImage));
            }

            if (folder.gallery) {
                setGalleryPreviews({
                    exterior: (folder.gallery.exterior || []).map((u: string) => getImageUrl(u)),
                    interior: (folder.gallery.interior || []).map((u: string) => getImageUrl(u)),
                    amenities: (folder.gallery.amenities || []).map((u: string) => getImageUrl(u)),
                    construction: (folder.gallery.construction || []).map((u: string) => getImageUrl(u)),
                    topPhotos: (folder.gallery.topPhotos || []).map((u: string) => getImageUrl(u)),
                    other: (folder.gallery.other || []).map((u: string) => getImageUrl(u)),
                });
            }

            if (folder.floorVariants && folder.floorVariants.length > 0) {
                // Group floor variants by Unit Type (e.g. 2 BHK, 3 BHK, 4 BHK)
                const grouped: { [key: string]: any[] } = {};
                folder.floorVariants.forEach((fv: any) => {
                    const uType = fv.unitType?.trim() || '2 BHK';
                    if (!grouped[uType]) grouped[uType] = [];
                    grouped[uType].push(fv);
                });

                const newFloorPlans = Object.entries(grouped).map(([uType, variants]) => {
                    const subMap: { [key: string]: any } = {};
                    variants.forEach((fv: any) => {
                        const vName = fv.subUnitVariant?.trim() || fv.title || 'Standard';
                        if (!subMap[vName]) {
                            subMap[vName] = {
                                subUnitType: vName,
                                subUnitVariant: vName,
                                price: '',
                                facing: [],
                                reraArea: '',
                                carpetArea: '',
                                builtUpArea: '',
                                superBuiltUpArea: '',
                                details: [{ label: '', value: '' }],
                                image: fv.url,
                                imagePreview: getImageUrl(fv.url),
                                image2D: fv.variantType === '3D' ? '' : fv.url,
                                image2DPreview: fv.variantType === '3D' ? '' : getImageUrl(fv.url),
                                image3D: fv.variantType === '3D' ? fv.url : '',
                                image3DPreview: fv.variantType === '3D' ? getImageUrl(fv.url) : '',
                                infoImage: '',
                                infoImagePreview: ''
                            };
                        } else {
                            if (fv.variantType === '3D') {
                                subMap[vName].image3D = fv.url;
                                subMap[vName].image3DPreview = getImageUrl(fv.url);
                            } else {
                                subMap[vName].image2D = fv.url;
                                subMap[vName].image2DPreview = getImageUrl(fv.url);
                                subMap[vName].image = fv.url;
                                subMap[vName].imagePreview = getImageUrl(fv.url);
                            }
                        }
                    });

                    const firstPrice = variants.find((v: any) => v.priceRange && v.priceRange !== 'On Request')?.priceRange;
                    return {
                        unitType: uType,
                        priceRange: firstPrice || 'On Request',
                        subUnits: Object.values(subMap)
                    };
                });

                setFloorPlans(newFloorPlans);
            }

            if (folder.documents?.legalDocuments?.length > 0) {
                setLegalDocuments(folder.documents.legalDocuments.map((doc: any) => ({
                    title: doc.title || 'Legal Document',
                    image: doc.url,
                    imagePreview: getImageUrl(doc.url)
                })));
            }

            const totalAssets = (folder.floorVariants?.length || 0) +
                Object.values(folder.gallery || {}).reduce((acc: number, a: any) => acc + (a?.length || 0), 0) +
                (folder.documents?.legalDocuments?.length || 0) + (folder.coverImage ? 1 : 0);

            toast.success(`Media Folder #${folder.folderNumber} connected! Loaded ${totalAssets} assets.`);
        } catch (err) {
            console.error("Apply media folder error:", err);
            toast.error("Failed to load media folder");
        } finally {
            setLoadingMediaFolder(false);
        }
    };

    const loadProfile = async () => {
        try {
            setLoadingProfile(true);
            const profile = await api.getUserProfile(token!);
            setUserProfile(profile);
            
            // Check if user needs a package and doesn't have one
            const needsPackage = profile.role === 'developer';
            const hasActivePackage = profile.assignedPackage && (!profile.packageExpiry || new Date(profile.packageExpiry) > new Date()) && profile.remainingProjects > 0;

            if (needsPackage && !hasActivePackage) {
                const packages = await api.getPackages('developer');
                setAvailablePackages(packages);
            }
        } catch (error) {
            console.error("Error loading profile:", error);
        } finally {
            setLoadingProfile(false);
        }
    };

    const handlePackageInquiry = async (packageId: string) => {
        if (submittingPkgId) return;
        try {
            setSubmittingPkgId(packageId);
            const res = await api.sendPackageInquiry(token!, packageId);
            if (res.success) {
                toast.success("Inquiry sent! Our team will contact you to activate your developer package.");
            } else {
                toast.error(res.message || "Failed to send inquiry");
            }
        } catch (error) {
            console.error("Package inquiry error:", error);
            toast.error("Server connection error.");
        } finally {
            setSubmittingPkgId(null);
        }
    };

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [amensRes, specsRes, devsRes, citiesRes, locsRes, scoreSettingsRes] = await Promise.all([
                    fetch(`${API_BASE}/amenities`).then(r => r.json()),
                    fetch(`${API_BASE}/specifications`).then(r => r.json()),
                    fetch(`${API_BASE}/developers`).then(r => r.json()),
                    fetch(`${API_BASE}/cities`).then(r => r.json()),
                    fetch(`${API_BASE}/localities`).then(r => r.json()),
                    fetch(`${API_BASE}/score-settings`).then(r => r.json()),
                ]);

                if (Array.isArray(amensRes)) setGlobalAmenities(amensRes);
                if (Array.isArray(specsRes)) setGlobalSpecs(specsRes);
                if (Array.isArray(devsRes)) setDevelopers(devsRes);
                if (Array.isArray(citiesRes)) setCities(citiesRes.filter((c: any) => c.isActive));
                if (Array.isArray(locsRes)) setAllLocalities(locsRes.filter((l: any) => l.isActive));
                if (scoreSettingsRes) setScoreSettings(scoreSettingsRes);
            } catch (err) {
                console.error("Initialization error:", err);
            }
        };

        fetchAll();

        api.getLandmarkCategories()
            .then(data => { 
                if (Array.isArray(data)) {
                    setGlobalLandmarkCategories(data);
                } else {
                    toast.error("Invalid format for landmark categories.");
                }
            })
            .catch(err => {
                console.error("Landmark categories fetch error (Add):", err);
                toast.error("Failed to sync landmark categories.");
            });

    }, [])

    useEffect(() => {
        if (isSlugManuallyEdited || !form.name) return;
        const slugify = (text: string) => {
            return text
                .toString()
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-\-+/g, '-')
                .replace(/^-+|-+$/g, '');
        };

        const namePart = slugify(form.name);
        const locationPart = form.location ? slugify(form.location) : '';
        const cityPart = form.city ? slugify(form.city) : '';

        const parts = [namePart, locationPart, cityPart].filter(Boolean);
        const newSlug = parts.join('-');

        setForm(prev => {
            if (prev.slug === newSlug) return prev;
            return { ...prev, slug: newSlug };
        });
    }, [form.name, form.location, form.city, isSlugManuallyEdited]);
    
    // Auto-sync Configuration to Floor Plans

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

        // Clear error for this field if it exists
        if (errors[name]) {
            setErrors(prev => {
                const newErrs = { ...prev };
                delete newErrs[name];
                return newErrs;
            });
        }

        if (name === 'propertyType') {
            const valStr = value as string;
            setForm(p => {
                const newTypes = p.propertyType.includes(valStr) 
                    ? p.propertyType.filter(t => t !== valStr) 
                    : [...p.propertyType, valStr];
                
                // Keep subcategories that are still valid for the selected property types
                const validSubs = new Set<string>();
                newTypes.forEach(t => {
                    (SUBCATEGORIES[t] || []).forEach(s => validSubs.add(s));
                });
                const newSubs = p.subCategory.filter(s => validSubs.has(s));
                
                return { ...p, propertyType: newTypes, subCategory: newSubs };
            });
        } else if (name === 'subCategory') {
            const valStr = value as string;
            setForm(p => {
                const newSubs = p.subCategory.includes(valStr)
                    ? p.subCategory.filter(s => s !== valStr)
                    : [...p.subCategory, valStr];
                return { ...p, subCategory: newSubs };
            });
        } else if (name === 'developerId') {
            const dev = developers.find(d => d._id === value);
            setForm(p => ({
                ...p,
                developerId: value,
                developerName: dev?.name || '',
                developerLogo: dev?.logo || p.developerLogo,
                developerWebsite: dev?.website || p.developerWebsite,
                totalProjects: dev?.totalProjects || '0',
                projectsCompleted: dev?.projectsCompleted || '0',
                projectsOngoing: dev?.projectsOngoing || '0',
                projectsUpcoming: dev?.projectsUpcoming || '0',
                projectsSince: dev?.projectsSince || ''
            }))
        } else if (name === 'city') {
            const cityName = value;
            setForm(p => ({ ...p, city: cityName, location: '' }));
            // Filter localities based on selected city
            const filtered = allLocalities.filter(l => {
                const cityObj = l.city;
                const cityMatch = (cityObj && typeof cityObj === 'object') ? cityObj.name === cityName : cityObj === cityName;
                return cityMatch;
            });
            setFilteredLocalities(filtered);
        } else if (name === 'timelineStatus') {
            const options = scoreSettings?.constructionStatusOptions?.length > 0 
                ? scoreSettings.constructionStatusOptions 
                : DEFAULT_CONSTRUCTION_STATUS_OPTIONS;
            const option = options.find((o: any) => o.label === value);
            
            // Logic for Required Documents in Step 5
            const getRequiredTitles = (status: string) => {
                if (status === 'RERA Approved') return ["Title Deed"];
                if (status === 'Under Construction' || status === 'Near Possession') return ["RERA Certificate", "Title Deed", "Approved Plan"];
                if (status === 'Ready to Move') return ["RERA Certificate", "Title Deed", "Approved Plan", "Occupancy Certificate (OC)"];
                return [];
            };

            const titles = getRequiredTitles(value);
            // Pre-fill legalDocuments state with these titles if they don't exist
            setLegalDocuments(prev => {
                const existing = [...prev];
                return titles.map(t => {
                    const found = existing.find(ex => ex.title === t);
                    return found ? found : { title: t, image: '', imagePreview: '', link: '' };
                });
            });

            setForm(p => ({
                ...p,
                timelineStatus: value,
                constructionStatusScore: option ? option.score : 0
            }));
        } else if (name === 'slug') {
            setIsSlugManuallyEdited(true);
            setForm(p => ({ ...p, slug: value }));
        } else if (name === 'name') {
            setForm(p => ({ ...p, name: value }));
        } else {
            setForm(p => ({ ...p, [name]: val }))
        }
    }

    const validateStep = (step: number) => {
        const newErrors: Record<string, boolean> = {};
        let isValid = true;

        if (step === 1) {
            if (!form.name.trim()) newErrors.name = true;
            if (!form.listingType) newErrors.listingType = true;
            if (form.propertyType.length === 0) newErrors.propertyType = true;
            if (form.subCategory.length === 0) newErrors.subCategory = true;
            if (!form.overviewConfiguration.trim()) newErrors.overviewConfiguration = true;
            if (!form.overviewPriceRange.trim()) newErrors.overviewPriceRange = true;
            if (!form.reraApproved) newErrors.reraApproved = true;
            if (!form.launchDate) newErrors.launchDate = true;
            if (!form.possessionDate) newErrors.possessionDate = true;
            if (form.reraApproved === 'Yes' && !form.reraNumber?.trim()) newErrors.reraNumber = true;
        } else if (step === 2) {
            if (!form.city) newErrors.city = true;
            if (!form.location) newErrors.location = true;
            if (!form.address.trim()) newErrors.address = true;
            if (!form.state.trim()) newErrors.state = true;
            if (!form.pinCode.trim()) newErrors.pinCode = true;
            if (!form.googleMapLocation.trim()) newErrors.googleMapLocation = true;
        } else if (step === 3) {
            if (!form.developerId) newErrors.developerId = true;
            if (!form.projectsSince.trim()) newErrors.projectsSince = true;
            if (!form.projectsCompleted.toString().trim()) newErrors.projectsCompleted = true;
            if (!form.projectsOngoing.toString().trim()) newErrors.projectsOngoing = true;
            if (!form.projectsUpcoming.toString().trim()) newErrors.projectsUpcoming = true;
        } else if (step === 4) {
            if (!form.overviewTotalPlotArea?.trim()) newErrors.overviewTotalPlotArea = true;
            if (!form.overviewAvgPricePerSqFt?.trim()) newErrors.overviewAvgPricePerSqFt = true;
            if (!form.overviewOpenSpacePercentage?.trim()) newErrors.overviewOpenSpacePercentage = true;
            if (!form.overviewConfiguration?.trim()) newErrors.overviewConfiguration = true;
            if (!form.overviewAmenitiesIcon?.trim()) newErrors.overviewAmenitiesIcon = true;
            if (!form.shortDescription?.trim()) newErrors.shortDescription = true;
            if (!form.fullDescription?.trim()) newErrors.fullDescription = true;
        } else if (step === 5) {
            if (!form.metaTitle?.trim()) newErrors.metaTitle = true;
            if (!form.slug?.trim()) newErrors.slug = true;
            if (!form.metaDescription?.trim()) newErrors.metaDescription = true;
            if (!form.focusKeyword?.trim()) newErrors.focusKeyword = true;
        } else if (step === 6) {
            const activePlans = floorPlans.filter(fp => fp.unitType.trim() !== '' || fp.subUnits.some(su => su.subUnitVariant.trim() !== ''));
            activePlans.forEach((fp, pIdx) => {
                if (!fp.unitType.trim()) newErrors[`floorPlan_${pIdx}_unitType`] = true;
                if (fp.subUnits.length === 0) {
                    newErrors[`floorPlan_${pIdx}_subUnits`] = true;
                } else {
                    fp.subUnits.forEach((su, sIdx) => {
                        if (!su.subUnitVariant.trim()) newErrors[`floorPlan_${pIdx}_subUnit_${sIdx}_variant`] = true;
                        if (!su.numberOfUnits?.trim()) newErrors[`floorPlan_${pIdx}_subUnit_${sIdx}_numberOfUnits`] = true;
                        if (!su.price.trim()) newErrors[`floorPlan_${pIdx}_subUnit_${sIdx}_price`] = true;
                    });
                }
            });
        } else if (step === 7) {
            // Optional: Media fields (featured image, brochure, gallery) are no longer strictly required to proceed.
        } else if (step === 8) {
            if (form.highlights.length === 0 || form.highlights.some(h => !h.text.trim())) newErrors.highlights = true;
            if (selectedAmenities.length === 0) newErrors.amenities = true;
            if (specifications.length === 0) newErrors.specifications = true;
            if (!form.contactNumber.trim()) newErrors.contactNumber = true;
            if (!form.disclaimer.trim()) newErrors.disclaimer = true;
        } else if (step === 9) {
            // Offers step validation if any
        } else if (step === 10) {
            // Review & Publish - no additional validation needed here
        }

        setErrors(newErrors);
        isValid = Object.keys(newErrors).length === 0;
        
        if (!isValid) {
            toast.error("Please fill all required fields highlighted in red.");
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        
        return isValid;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(s => Math.min(s + 1, 10));
        }
    };
    const prevStep = () => {
        setErrors({}); // Clear errors when going back
        setCurrentStep(s => Math.max(s - 1, 1));
    };


    const handleUpload = async (files: File[]) => {
        if (!token || files.length === 0) return []
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('images', file));
            
            const res = await fetch(`${API_BASE}/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            
            const data = await res.json();
            
            if (!res.ok) {
                toast.error(`Upload failed: ${data.message || 'Server error'}`);
                return [];
            }
            
            return data.urls || []
        } catch (err) {
            console.error('Upload failed', err)
            toast.error('Connection error during upload. Check file size.');
            return []
        }
    }

    const handleSubmit = async (e?: React.FormEvent, statusOverride?: string, silent?: boolean) => {
        if (e) e.preventDefault()
        if (!token) {
            if (!silent) setMsg({ type: 'error', text: 'You are not logged in. Please log in to save your progress.' })
            return false
        }
        
        const isDraft = statusOverride === 'Draft'
        if (!silent) setSubmitting(true)

        // Comprehensive Validation
        if (!isDraft) {
            // First check all previous steps if somehow skipped, but mainly focus on current and general
            let firstErrorStep = -1;
            for (let i = 1; i <= 10; i++) { // Validating all 10 steps
                if (!validateStep(i)) {
                    firstErrorStep = i;
                    break;
                }
            }

            if (firstErrorStep !== -1) {
                setCurrentStep(firstErrorStep);
                if (!silent) setSubmitting(false);
                return false;
            }
        } else {
            // Even for draft, we need a name
            if (!form.name.trim()) {
                setErrors({ name: true });
                setMsg({ type: 'error', text: 'Please provide a Project Name to save as draft.' })
                setCurrentStep(1)
                window.scrollTo(0, 0)
                if (!silent) setSubmitting(false)
                return false
            }
        }

        try {
            // 1. Upload Featured Image
            let featuredUrl = form.featuredImage || ''
            if (featuredImage) {
                const urls = await handleUpload([featuredImage])
                featuredUrl = urls[0] || ''
            } else if (!featuredUrl && featuredPreview) {
                featuredUrl = featuredPreview;
            }

            // 2. Upload Gallery
            const galleryUrls: Record<string, string[]> = {};
            for (const category of Object.keys(gallery)) {
                const uploaded = await handleUpload(gallery[category]);
                const existing = (galleryPreviews[category] || []).filter(u => typeof u === 'string' && !u.startsWith('blob:'));
                galleryUrls[category] = [...existing, ...uploaded];
            }

            // 2.5. Upload TP Image
            let tpUrl = form.tpImageUrl || form.tpImage || ''
            if (tpImage) {
                const urls = await handleUpload([tpImage])
                tpUrl = urls[0] || ''
            }

            // 3. Upload Brochure
            let brochureUrl = form.brochureUrl || ''
            if (brochureFile) {
                const fd = new FormData()
                fd.append('document', brochureFile)
                const res = await fetch(`${API_BASE}/upload/document`, {
                    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
                })
                const data = await res.json()
                brochureUrl = data.url || ''
            }
            
            // 3.5. Upload Project Video
            let projectVideoUrl = form.projectVideoUrl || ''
            if (projectVideoFile) {
                const fd = new FormData()
                fd.append('document', projectVideoFile)
                const res = await fetch(`${API_BASE}/upload/document`, {
                    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
                })
                const data = await res.json()
                projectVideoUrl = data.url || ''
            }

            // 4. Upload Construction Status Image
            let constructionStatusImageUrl = ''
            if (constructionStatusImage) {
                const urls = await handleUpload([constructionStatusImage])
                constructionStatusImageUrl = urls[0] || ''
            }

            // 5. Upload SEO Image
            let seoImageUrlStr = form.seoImageUrl || ''
            if (seoImageFile) {
                const urls = await handleUpload([seoImageFile])
                seoImageUrlStr = urls[0] || ''
            }

            // 5.5 Upload custom amenities icons
            const processedCustomAmenities = await Promise.all((form.customAmenities || []).map(async ca => {
                if (ca.file) {
                    const urls = await handleUpload([ca.file]);
                    return { name: ca.name, icon: urls[0] || ca.icon };
                }
                return { name: ca.name, icon: ca.icon };
            }));

            // 6. Construct Final Payload
            const payload = {
                ...form,
                amenityIcons: form.amenityIcons,
                customAmenities: processedCustomAmenities,
                status: statusOverride || 'Pending',
                developer: form.developerId || undefined,
                developerDetails: {
                    name: form.developerName,
                    coDeveloper: form.coDeveloper,
                    logo: form.developerLogo,
                    website: form.developerWebsite,
                    description: form.developerDescription,
                    tagline: form.developerTagline,
                    totalProjects: form.totalProjects,
                    projectsCompleted: form.projectsCompleted,
                    projectsOngoing: form.projectsOngoing,
                    projectsUpcoming: form.projectsUpcoming,
                    projectsSince: form.projectsSince
                },
                overview: {
                    configuration: form.overviewConfiguration,
                    sizeRange: form.overviewSizeRange,
                    priceRange: form.overviewPriceRange,
                    avgPricePerSqFt: form.overviewAvgPricePerSqFt,
                    projectArea: form.overviewTotalPlotArea || form.overviewProjectArea,
                    totalPlotArea: form.overviewTotalPlotArea,
                    openSpacePercentage: form.overviewOpenSpacePercentage,
                    totalTowers: form.overviewTotalTowers,
                    totalFloors: form.overviewTotalFloors,
                    totalUnits: form.overviewTotalUnits,
                    floors: form.overviewFloors,
                    bathrooms: form.overviewBathrooms,
                    ageOfConstruction: form.overviewAgeOfConstruction,
                    parking: form.overviewParking,
                    amenitiesIcon: form.overviewAmenitiesIcon
                },
                mapDetails: {
                    googleMapLocation: form.googleMapLocation,
                    google3dViewLink: form.google3dViewLink,
                    latitude: form.latitude,
                    longitude: form.longitude,
                },
                virtualTours: form.virtualTours.filter(vt => vt.link),
                youtubeVideos: form.youtubeVideos.filter(yv => yv.link),
                inventory: inventory.filter(inv => inv.tower || inv.unitNo),

                paymentPlans: form.paymentPlans,
                bankApprovals: form.bankApprovals,
                highlights: await Promise.all(form.highlights.map(async h => {
                    let iconUrl = h.icon;
                    if (h.file) {
                        const urls = await handleUpload([h.file]);
                        iconUrl = urls[0] || iconUrl;
                    }
                    return { icon: iconUrl, text: h.text };
                })),
                metaTitle: form.metaTitle,
                metaDescription: form.metaDescription,
                metaKeywords: form.metaKeywords,
                canonicalUrl: form.canonicalUrl,
                focusKeyword: form.focusKeyword,
                seoImageUrl: seoImageUrlStr,
                contactNumber: form.contactNumber,
                contactPhone: form.contactPhone,
                whatsappNumber: form.whatsappNumber,
                contactEmail: form.contactEmail,
                disclaimer: form.disclaimer,
                isOurPick: form.isOurPick,
                subCategory: form.subCategory,
                projectStatus: form.projectStatus,
                progressPercentage: form.progressPercentage,
                tagline: form.tagline,
                city: form.city,
                location: form.location,
                address: form.address,
                state: form.state,
                country: form.country,
                pinCode: form.pinCode,
                mediaFolderNumber: form.mediaFolderNumber || (appliedMediaFolder ? appliedMediaFolder.folderNumber : undefined),
                slug: form.slug.trim() || form.name.trim().replace(/\s+/g, '-').replace(/[^\w-]+/g, ''),

                launchDate: form.launchDate || undefined,
                possessionDate: form.possessionDate || undefined,
                constructionStatus: form.timelineStatus,
                constructionStatusScore: form.constructionStatusScore,
                constructionStatusImage: constructionStatusImageUrl,
                featuredImage: featuredUrl,
                tpImage: tpUrl,
                gallery: galleryUrls,
                galleryIcons: galleryIcons,
                brochureUrl: brochureUrl,
                projectVideoUrl: projectVideoUrl,
                legalDocuments: await Promise.all(legalDocuments.filter(ld => ld.title || ld.imagePreview || ld.link).map(async ld => ({
                    title: ld.title,
                    image: ld.file ? (await handleUpload([ld.file]))[0] : ld.image,
                    link: ld.link
                }))),
                shortDescription: form.shortDescription,
                description: form.fullDescription,
                nearbyPlaces: form.nearbyPlaces,
                builderOffers: form.builderOffers.filter(offer => offer.offerType),
                investmentPotential: form.investmentPotential,
                additionalDetails: additionalDetails.filter(d => d.label),
                floorPlans: await Promise.all(floorPlans.filter(fp => fp.unitType.trim() !== '' || fp.subUnits.some(su => su.subUnitVariant.trim() !== '')).map(async fp => ({
                    unitType: fp.unitType,
                    subUnits: await Promise.all(fp.subUnits.map(async su => ({
                        subUnitType: su.subUnitType,
                        subUnitVariant: su.subUnitVariant,
                        price: su.price,
                        facing: su.facing,
                        reraArea: su.reraArea,
                        carpetArea: su.carpetArea,
                        builtUpArea: su.builtUpArea,
                        superBuiltUpArea: su.superBuiltUpArea,
                        details: su.details.filter(d => d.label),
                        image: su.imageFile ? (await handleUpload([su.imageFile]))[0] : su.image,
                        image2D: su.image2DFile ? (await handleUpload([su.image2DFile]))[0] : su.image2D,
                        image3D: su.image3DFile ? (await handleUpload([su.image3DFile]))[0] : su.image3D,
                        infoImage: su.infoImageFile ? (await handleUpload([su.infoImageFile]))[0] : su.infoImage,
                        virtual3DLink: su.virtual3DLink || ''
                    })))
                }))),
                sectionInfo: form.sectionInfo,
                amenities: selectedAmenities,
                faqs: form.faq.filter(f => f.question && f.answer),
                specifications: await Promise.all(specifications.map(async s => {
                    let iconUrl = s.icon;
                    if (s.file) {
                        const urls = await handleUpload([s.file]);
                        iconUrl = urls[0] || iconUrl;
                    }
                    return {
                        category: s.category,
                        details: s.details,
                        icon: iconUrl
                    };
                })),

                // Calculated Trust Scores for Submission
                builderTrustScore: (() => {
                    const dev = developers.find(d => d._id === form.developerId);
                    if (!dev) return 0;
                    let s = 0;
                    const years = new Date().getFullYear() - parseInt(dev.projectsSince || '2026');
                    if (years >= 20) s += 5; else if (years >= 15) s += 4; else if (years >= 10) s += 3; else if (years >= 5) s += 2; else s += 1;
                    const comp = parseInt(dev.projectsCompleted || '0');
                    if (comp >= 50) s += 4; else if (comp >= 30) s += 3; else if (comp >= 15) s += 2; else if (comp >= 5) s += 1; else s += 0.5;
                    const ong = parseInt(dev.projectsOngoing || '0');
                    if (ong >= 20) s += 3; else if (ong >= 10) s += 2; else if (ong >= 5) s += 1; else s += 0.5;
                    if (dev.deliveryRecord === 'on-time') s += 2; else if (dev.deliveryRecord === 'minor-delay') s += 1;
                    const upc = parseInt(dev.projectsUpcoming || '0');
                    if (upc >= 10) s += 1; else if (upc >= 5) s += 0.7; else s += 0.5;
                    return s;
                })(),
                localityTrustScore: (() => {
                    let score = 0;
                    const rules = scoreSettings?.localityScoreRules || [];
                    const usedCategories = new Set();
                    
                    rules.forEach((rule: any) => {
                        if (!usedCategories.has(rule.category)) {
                            const hasMatch = form.nearbyPlaces.some(place => {
                                const distance = parseFloat(place.distance) || 0;
                                return place.category === rule.category && distance > 0 && distance <= rule.maxDistanceKm;
                            });
                            
                            if (hasMatch) {
                                score += rule.points;
                                usedCategories.add(rule.category);
                            }
                        }
                    });
                    return score;
                })(),
                loadingScore: (() => {
                    const loadingScores = floorPlans.flatMap(fp => fp.subUnits).map(su => {
                        const carpet = parseFloat(su.carpetArea);
                        const superB = parseFloat(su.superBuiltUpArea);
                        if (carpet && superB && superB > carpet) {
                            const pct = ((superB - carpet) / superB) * 100;
                            if (pct <= 30) return 5; if (pct <= 35) return 4; if (pct <= 40) return 3; if (pct <= 45) return 2; return 1;
                        }
                        return 0;
                    }).filter(s => s > 0);
                    return loadingScores.length > 0 ? parseFloat((loadingScores.reduce((a: number, b: number) => a + b, 0) / loadingScores.length).toFixed(1)) : 0;
                })(),
                legalTrustScore: (() => {
                    const rules = scoreSettings?.legalTrustRules || [];
                    let score = 0;
                    
                    rules.forEach((rule: any) => {
                        if (!rule.enabled) return;
                        
                        switch (rule.key) {
                            case 'timelineStatus':
                                if (form.timelineStatus) score += rule.points;
                                break;
                            case 'reraApproved':
                                if (form.reraApproved === 'Yes') score += rule.points;
                                break;
                            case 'launchDate':
                                if (form.launchDate) score += rule.points;
                                break;
                            case 'possessionDate':
                                if (form.possessionDate) score += rule.points;
                                break;
                        }
                    });
                    
                    return score;
                })(),
                amenitiesScore: selectedAmenities.reduce((acc, id) => acc + (globalAmenities.find(a => a._id === id)?.score || 0), 0),
                specificationsScore: Math.min(specifications.length, 3)
            }

            const url = activeProjectId ? `${API_BASE}/projects/${activeProjectId}` : `${API_BASE}/projects`
            const method = activeProjectId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload)
            })

            const responseData = await res.json()
            if (res.ok) {
                if (!activeProjectId && responseData._id) {
                    setActiveProjectId(responseData._id)
                }

                // 6. Synchronize Inventory
                const syncProjectId = activeProjectId || responseData._id;
                if (syncProjectId && inventory.length > 0) {
                    await api.bulkSyncInventory(token, syncProjectId, inventory);
                }

                if (!silent) {
                    setMsg({ type: 'success', text: isDraft ? 'Draft saved successfully!' : 'Project created successfully!' })
                    if (!isDraft) setTimeout(() => navigate('/admin-projects'), 2000)
                }
                return true
            } else {
                if (!silent) setMsg({ type: 'error', text: responseData.message || responseData.error || 'Failed to process project' })
                return false
            }
        } catch (err) {
            if (!silent) setMsg({ type: 'error', text: 'Server error occurred' })
            return false
        } finally {
            if (!silent) setSubmitting(false)
        }
    }

    const handleSaveDraft = async () => {
        await handleSubmit(undefined, 'Draft')
    }

    const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setImporting(true);
            const data = await parseProjectExcel(file);
            if (data.length === 0) {
                toast.error("Excel file is empty or invalid format.");
                setImporting(false);
                return;
            }

            setPendingImportData(data);
            setShowImportConfirm(true);
            setImporting(false);
        } catch (err) {
            console.error("Import error:", err);
            toast.error("Failed to parse Excel file.");
            setImporting(false);
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }

    const loadProjectIntoForm = async (proj: any) => {
        try {
            setImporting(true);
            setShowImportConfirm(false);

            // 1. Populate general form fields
            setForm(prev => ({
                ...prev,
                name: proj.name || prev.name,
                listingType: proj.listingType || prev.listingType,
                propertyType: proj.propertyType ? (Array.isArray(proj.propertyType) ? proj.propertyType : [proj.propertyType]) : prev.propertyType,
                subCategory: proj.subCategory ? (Array.isArray(proj.subCategory) ? proj.subCategory : [proj.subCategory]) : prev.subCategory,
                projectStatus: proj.projectStatus || prev.projectStatus,
                city: proj.city || prev.city,
                location: proj.location || prev.location,
                address: proj.address || prev.address,
                state: proj.state || prev.state,
                country: proj.country || prev.country,
                pinCode: proj.pinCode || prev.pinCode,
                latitude: proj.latitude || prev.latitude,
                longitude: proj.longitude || prev.longitude,
                googleMapLocation: proj.googleMapLocation || prev.googleMapLocation,
                google3dViewLink: proj.google3dViewLink || prev.google3dViewLink,
                developerName: proj.developerName || prev.developerName,
                coDeveloper: proj.coDeveloper || prev.coDeveloper,
                developerDescription: proj.developerDescription || prev.developerDescription,
                projectsSince: proj.projectsSince || prev.projectsSince,
                totalProjects: proj.totalProjects || prev.totalProjects,
                projectsCompleted: proj.projectsCompleted || prev.projectsCompleted,
                projectsOngoing: proj.projectsOngoing || prev.projectsOngoing,
                projectsUpcoming: proj.projectsUpcoming || prev.projectsUpcoming,
                overviewConfiguration: proj.overviewConfiguration || prev.overviewConfiguration,
                overviewTotalPlotArea: proj.overviewTotalPlotArea || prev.overviewTotalPlotArea,
                overviewAmenitiesIcon: proj.overviewAmenitiesIcon || prev.overviewAmenitiesIcon,
                overviewAvgPricePerSqFt: proj.overviewAvgPricePerSqFt || prev.overviewAvgPricePerSqFt,
                overviewOpenSpacePercentage: proj.overviewOpenSpacePercentage || prev.overviewOpenSpacePercentage,
                overviewTotalTowers: proj.overviewTotalTowers || prev.overviewTotalTowers,
                overviewTotalFloors: proj.overviewTotalFloors || prev.overviewTotalFloors,
                overviewFloors: proj.overviewFloors || prev.overviewFloors,
                overviewTotalUnits: proj.overviewTotalUnits || prev.overviewTotalUnits,
                progressPercentage: parseFloat(proj.progressPercentage) || prev.progressPercentage,
                reraNumber: proj.reraNumber || prev.reraNumber,
                reraApproved: proj.reraNumber ? 'Yes' : prev.reraApproved,
                overviewBathrooms: proj.overviewBathrooms || prev.overviewBathrooms,
                overviewAgeOfConstruction: proj.overviewAgeOfConstruction || prev.overviewAgeOfConstruction,
                overviewParking: proj.overviewParking || prev.overviewParking,
                overviewSizeRange: proj.overviewSizeRange || prev.overviewSizeRange,
                overviewPriceRange: proj.overviewPriceRange || prev.overviewPriceRange,
                launchDate: proj.launchDate || prev.launchDate,
                possessionDate: proj.possessionDate || prev.possessionDate,
                timelineStatus: proj.projectPhase || prev.timelineStatus,
                shortDescription: proj.shortDescription || prev.shortDescription,
                fullDescription: proj.fullDescription || prev.fullDescription,
                metaTitle: proj.metaTitle || prev.metaTitle,
                metaKeywords: proj.metaKeywords || prev.metaKeywords,
                metaDescription: proj.metaDescription || prev.metaDescription,
                focusKeyword: proj.focusKeyword || prev.focusKeyword,
                videoUrl: proj.videoUrl || prev.videoUrl,
                brochureUrl: proj.brochureUrl || prev.brochureUrl,
                disclaimer: proj.disclaimer || prev.disclaimer,
                highlights: proj.highlights ? String(proj.highlights).split('|').map(h => ({ icon: 'fa-star', text: h.trim() })) : prev.highlights,
            }));

            // 2. Floor variants from excel if provided
            const excelSubUnits: any[] = [];
            if (proj.floorVariantA) excelSubUnits.push({ subUnitType: 'Type A', subUnitVariant: proj.floorVariantA, price: '', facing: [], reraArea: '', carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '', infoImage: '', infoImagePreview: '' });
            if (proj.floorVariantB) excelSubUnits.push({ subUnitType: 'Type B', subUnitVariant: proj.floorVariantB, price: '', facing: [], reraArea: '', carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '', infoImage: '', infoImagePreview: '' });
            if (proj.floorVariantC) excelSubUnits.push({ subUnitType: 'Type C', subUnitVariant: proj.floorVariantC, price: '', facing: [], reraArea: '', carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '', infoImage: '', infoImagePreview: '' });
            if (proj.floorVariantD) excelSubUnits.push({ subUnitType: 'Type D', subUnitVariant: proj.floorVariantD, price: '', facing: [], reraArea: '', carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '', infoImage: '', infoImagePreview: '' });
            if (proj.floorVariantE) excelSubUnits.push({ subUnitType: 'Type E', subUnitVariant: proj.floorVariantE, price: '', facing: [], reraArea: '', carpetArea: '', builtUpArea: '', superBuiltUpArea: '', details: [{ label: '', value: '' }], image: '', imagePreview: '', image2D: '', image2DPreview: '', image3D: '', image3DPreview: '', infoImage: '', infoImagePreview: '' });

            if (excelSubUnits.length > 0) {
                setFloorPlans([{
                    unitType: proj.overviewConfiguration || 'Standard',
                    subUnits: excelSubUnits
                }]);
            }

            // 3. Amenities
            if (proj.amenities) {
                const mappedAmenities: string[] = [];
                String(proj.amenities).split(',').forEach(a => {
                    const name = a.trim();
                    if (!name) return;
                    const found = globalAmenities.find(ga => ga.name.toLowerCase() === name.toLowerCase());
                    if (found) mappedAmenities.push(found._id);
                });
                if (mappedAmenities.length > 0) setSelectedAmenities(mappedAmenities);
            }

            // 4. Specifications
            if (proj.specifications) {
                const mappedSpecs = String(proj.specifications).split('|').map(spec => {
                    const parts = spec.split(':');
                    return { category: parts[0]?.trim() || 'General', details: parts.slice(1).join(':').trim() || '', icon: 'fa-check' };
                });
                setSpecifications(mappedSpecs);
            }

            // 5. FAQs
            if (proj.faq) {
                const mappedFaqs = String(proj.faq).split('|').map(f => {
                    const parts = f.split(':');
                    return { question: parts[0]?.trim() || '', answer: parts.slice(1).join(':').trim() || '' };
                });
                setFaqs(mappedFaqs);
            }

            // 6. Nearby Landmarks
            if (proj.nearbyPlaces) {
                const mappedPlaces = String(proj.nearbyPlaces).split('|').map(place => {
                    const parts = place.split(',');
                    return { name: parts[0]?.trim() || '', distance: parts[1]?.trim() || '', category: parts[2]?.trim() || 'Schools' };
                });
                setForm(prev => ({ ...prev, nearbyPlaces: mappedPlaces }));
            }

            // 7. AUTO-ASSIGN & APPLY MEDIA FOLDER
            const rawFolder = proj.mediaFolderNumber;
            if (rawFolder !== undefined && rawFolder !== null && String(rawFolder).trim() !== '') {
                const match = String(rawFolder).match(/\d+/);
                const folderNum = match ? parseInt(match[0], 10) : parseInt(String(rawFolder), 10);
                if (!isNaN(folderNum) && folderNum > 0) {
                    setMediaFolderInput(String(folderNum));
                    setForm(prev => ({ ...prev, mediaFolderNumber: folderNum }));
                    await handleApplyMediaFolder(folderNum);
                }
            }

            toast.success(`Project "${proj.name || 'data'}" loaded into form with Media Folder assigned!`);
        } catch (err) {
            console.error("Error loading project into form:", err);
            toast.error("Failed to load project into form.");
        } finally {
            setImporting(false);
            setPendingImportData([]);
        }
    };

    const processBulkImport = async () => {
        try {
            setImporting(true);
            setShowImportConfirm(false);
            
            const safeDate = (val: any) => {
                if (!val) return undefined;
                const d = new Date(val);
                return isNaN(d.getTime()) ? undefined : d;
            };

            const projectsToImport = pendingImportData.map(proj => {
                const name = (proj.name?.toString() || '').trim() || 'Untitled Project';
                
                const cleanSlugPart = (str: string) => {
                    return (str || '')
                        .toString()
                        .toLowerCase()
                        .trim()
                        .replace(/\s+/g, '-')
                        .replace(/[^\w\-]+/g, '')
                        .replace(/\-\-+/g, '-')
                        .replace(/^-+|-+$/g, '');
                };

                const namePart = cleanSlugPart(name);
                const locationPart = cleanSlugPart(proj.location);
                const cityPart = cleanSlugPart(proj.city);

                let slug = namePart;
                if (locationPart) {
                    slug += `-${locationPart}`;
                }
                if (cityPart) {
                    slug += `-${cityPart}`;
                }
                if (!slug) {
                    slug = 'untitled-project';
                }

                
                // Handle Floor Variants
                const floorPlans: any[] = [];
                const subUnits: any[] = [];
                if (proj.floorVariantA) subUnits.push({ subUnitType: 'Type A', subUnitVariant: proj.floorVariantA });
                if (proj.floorVariantB) subUnits.push({ subUnitType: 'Type B', subUnitVariant: proj.floorVariantB });
                if (proj.floorVariantC) subUnits.push({ subUnitType: 'Type C', subUnitVariant: proj.floorVariantC });
                if (proj.floorVariantD) subUnits.push({ subUnitType: 'Type D', subUnitVariant: proj.floorVariantD });
                if (proj.floorVariantE) subUnits.push({ subUnitType: 'Type E', subUnitVariant: proj.floorVariantE });
                
                if (subUnits.length > 0) {
                    floorPlans.push({
                        unitType: proj.overviewConfiguration || 'Standard',
                        subUnits: subUnits
                    });
                }

                // Additional Details for Links
                const additionalDetails: any[] = [];
                if (proj.titleDeedLink) additionalDetails.push({ label: 'Title Deed Link', value: proj.titleDeedLink, icon: 'fa-file-contract' });
                if (proj.approvedPlanLink) additionalDetails.push({ label: 'Approved Plan Link', value: proj.approvedPlanLink, icon: 'fa-map' });

                // Process Amenities
                const mappedAmenities: string[] = [];
                const mappedCustomAmenities: any[] = [];
                if (proj.amenities) {
                    String(proj.amenities).split(',').forEach(a => {
                        const name = a.trim();
                        if (!name) return;
                        const found = globalAmenities.find(ga => ga.name.toLowerCase() === name.toLowerCase());
                        if (found) {
                            mappedAmenities.push(found._id);
                        } else {
                            mappedCustomAmenities.push({ name: name, icon: '' });
                        }
                    });
                }

                return {
                    name,
                    slug,
                    status: 'Draft',
                    mediaFolderNumber: (() => {
                        if (!proj.mediaFolderNumber) return undefined;
                        const m = String(proj.mediaFolderNumber).match(/\d+/);
                        return m ? parseInt(m[0], 10) : undefined;
                    })(),
                    propertyType: proj.propertyType || 'Residential',
                    listingType: (() => {
                        const val = (proj.listingType || '').toString().toLowerCase();
                        if (val.includes('sale')) return 'For Sale';
                        if (val.includes('rent')) return 'For Rent';
                        if (val.includes('both')) return 'Both';
                        return 'For Sale';
                    })(),
                    subCategory: proj.subCategory || '',
                    projectStatus: (() => {
                        const val = (proj.projectPhase || '').toString().toLowerCase();
                        if (val.includes('under') || val.includes('construction')) return 'Under Construction';
                        if (val.includes('ready') || val.includes('move')) return 'Ready to Move';
                        if (val.includes('new') || val.includes('launch')) return 'Newly Launched';
                        return 'Newly Launched';
                    })(),
                    constructionStatus: proj.projectPhase || 'Newly Launched',
                    tagline: proj.tagline || '',
                    city: proj.city || '',
                    location: proj.location || '',
                    address: proj.address || '',
                    state: proj.state || '',
                    country: proj.country || '',
                    pinCode: proj.pinCode || '',
                    progressPercentage: parseFloat(proj.progressPercentage) || 0,
                    isOurPick: proj.isOurPick?.toString().toLowerCase() === 'yes',
                    launchDate: safeDate(proj.launchDate),
                    possessionDate: safeDate(proj.possessionDate),
                    reraNumber: proj.reraNumber || '',
                    reraApproved: proj.reraNumber ? 'Yes' : 'No',
                    reraLink: proj.reraLink,
                    shortDescription: proj.shortDescription || '',
                    description: proj.fullDescription || '',
                    metaTitle: proj.metaTitle || '',
                    metaDescription: proj.metaDescription || '',
                    metaKeywords: proj.metaKeywords || '',
                    canonicalUrl: proj.canonicalUrl || '',
                    focusKeyword: proj.focusKeyword || '',
                    videoUrl: proj.videoUrl || '',
                    brochureUrl: proj.brochureUrl || '',
                    contactNumber: proj.contactNumber || '',
                    contactEmail: proj.contactEmail || '',
                    investmentPotential: proj.investmentPotential || '',
                    overview: {
                        configuration: proj.overviewConfiguration || '',
                        sizeRange: proj.overviewSizeRange || '',
                        priceRange: proj.overviewPriceRange || '',
                        projectArea: proj.overviewTotalPlotArea || '',
                        totalPlotArea: proj.overviewTotalPlotArea || '',
                        avgPricePerSqFt: proj.overviewAvgPricePerSqFt || '',
                        openSpacePercentage: proj.overviewOpenSpacePercentage || '',
                        totalTowers: proj.overviewTotalTowers || '',
                        totalFloors: proj.overviewTotalFloors || '',
                        floors: proj.overviewFloors || '',
                        totalUnits: proj.overviewTotalUnits || '',
                        bathrooms: proj.overviewBathrooms || '',
                        ageOfConstruction: proj.overviewAgeOfConstruction || '',
                        parking: proj.overviewParking || '',
                        amenitiesIcon: proj.overviewAmenitiesIcon || ''
                    },
                    mapDetails: {
                        latitude: proj.latitude || '',
                        longitude: proj.longitude || '',
                        googleMapLocation: proj.googleMapLocation || '',
                        google3dViewLink: proj.google3dViewLink || ''
                    },
                    developerDetails: {
                        name: proj.developerName || '',
                        coDeveloper: proj.coDeveloper || '',
                        website: proj.developerWebsite || '',
                        tagline: proj.developerTagline || '',
                        description: proj.developerDescription || '',
                        projectsSince: proj.projectsSince || '',
                        totalProjects: proj.totalProjects || '',
                        projectsCompleted: proj.projectsCompleted || '0',
                        projectsOngoing: proj.projectsOngoing || '0',
                        projectsUpcoming: proj.projectsUpcoming || '0'
                    },
                    highlights: proj.highlights ? String(proj.highlights).split('|').map(h => ({ icon: 'fa-star', text: h.trim() })) : [],
                    amenities: mappedAmenities,
                    customAmenities: mappedCustomAmenities,
                    nearbyPlaces: proj.nearbyPlaces ? String(proj.nearbyPlaces).split('|').map(place => {
                        const parts = place.split(',');
                        return { name: parts[0]?.trim() || '', distance: parts[1]?.trim() || '', category: parts[2]?.trim() || 'Landmark' };
                    }) : [],
                    specifications: proj.specifications ? String(proj.specifications).split('|').map(spec => {
                        const parts = spec.split(':');
                        return { category: parts[0]?.trim() || '', details: parts.slice(1).join(':').trim() || '', icon: 'fa-check' };
                    }) : [],
                    faqs: proj.faq ? String(proj.faq).split('|').map(f => {
                        const parts = f.split(':');
                        return { question: parts[0]?.trim() || '', answer: parts.slice(1).join(':').trim() || '' };
                    }) : [],
                    disclaimer: proj.disclaimer || '',
                    builderOffers: proj.builderOffers ? String(proj.builderOffers).split('|').map(offer => {
                        const parts = offer.split(':');
                        return { offerType: parts[0]?.trim() || '', example: parts[1]?.trim() || '', priceImpact: parts[2]?.trim() || '', startDate: parts[3]?.trim() || '', tillDate: parts[4]?.trim() || '' };
                    }) : [],
                    paymentPlans: proj.paymentPlans ? String(proj.paymentPlans).split('|').map(plan => {
                        const parts = plan.split(':');
                        return { title: parts[0]?.trim() || '', description: parts.slice(1).join(':').trim() || '' };
                    }) : [],
                    bankApprovals: proj.bankApprovals ? String(proj.bankApprovals).split(',').map(b => b.trim()) : [],
                    legalDocuments: proj.legalDocuments ? String(proj.legalDocuments).split('|').map(doc => {
                        const parts = doc.split(':');
                        return { title: parts[0]?.trim() || '', link: parts.slice(1).join(':').trim() || '' };
                    }) : [],
                    dynamicSlots: proj.dynamicSlots ? String(proj.dynamicSlots).split('|').map(slot => {
                        const parts = slot.split(':');
                        return { title: parts[0]?.trim() || '', contentType: (parts[1]?.trim() as any) || 'HTML Content', content: parts.slice(2).join(':').trim() || '' };
                    }) : [],
                    floorPlans,
                    additionalDetails
                };
            });
            
            const res = await fetch(`${API_BASE}/projects/bulk`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    Authorization: `Bearer ${token}` 
                },
                body: JSON.stringify({ projects: projectsToImport })
            });

            const result = await res.json();
            if (res.ok) {
                if (result.successCount > 0 && result.errorCount === 0) {
                    toast.success(result.message);
                    navigate('/admin-projects');
                } else if (result.successCount > 0 && result.errorCount > 0) {
                    toast.warning(`Imported ${result.successCount} projects, but ${result.errorCount} failed. Check console for details.`);
                    console.table(result.results.filter((r: any) => !r.success));
                    setTimeout(() => navigate('/admin-projects'), 3000);
                } else {
                    toast.error(result.message || "Bulk import failed. No projects were imported.");
                }
            } else {
                toast.error(result.message || "Bulk import failed due to server error.");
            }
        } catch (err) {
            console.error("Bulk import error:", err);
            toast.error("An error occurred during bulk import.");
        } finally {
            setImporting(false);
            setPendingImportData([]);
        }
    }



    const handleNextWithDraft = async () => {
        // First validate the current step
        if (!validateStep(currentStep)) return

        // We use silent=false here if we want the user to see why it failed
        const success = await handleSubmit(undefined, 'Draft', false)
        if (success) {
            nextStep()
            window.scrollTo(0, 0)
        }
    }


    const needsPackage = userProfile && userProfile.role === 'developer';
    const hasActivePackage = userProfile && userProfile.assignedPackage && 
                           (!userProfile.packageExpiry || new Date(userProfile.packageExpiry) > new Date()) && 
                           userProfile.remainingProjects > 0;

    if (loadingProfile) {
        return (
            <AdminLayout>
                <div className="py-20 text-center">
                    <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-bold text-[#05144b] uppercase tracking-widest text-xs">Verifying Developer Access...</p>
                </div>
            </AdminLayout>
        );
    }

    if (needsPackage && !hasActivePackage) {
        return (
            <AdminLayout>
                <div className="mb-10 text-center max-w-2xl mx-auto">
                    <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fa-solid fa-gem text-amber-500 text-3xl"></i>
                    </div>
                    <h2 className="text-3xl font-black text-black mb-4">Project Listing Package Required</h2>
                    <p className="text-black font-medium">To launch new projects on Property Guru, you need an active developer subscription. Select a plan to continue.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-10">
                    {availablePackages.map((pkg) => (
                        <div key={pkg._id} className={`bg-white dark:bg-slate-800 rounded-[2.5rem] border-2 ${pkg.popular ? 'border-blue-500 shadow-xl shadow-blue-50/10' : 'border-blue-900/10 dark:border-slate-700'} p-8 relative overflow-hidden transition-all group`}>
                            {pkg.popular && <div className="absolute top-6 right-[-35px] bg-blue-600 text-white text-[10px] font-black py-1 px-10 rotate-45 uppercase tracking-widest">Premium</div>}
                            <div className="flex items-center gap-4 mb-6">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl shadow-sm`} style={{ backgroundColor: pkg.color + '15', color: pkg.color }}>
                                    <i className={`fa-solid ${pkg.icon || 'fa-city'}`}></i>
                                </div>
                                <div>
                                    <h4 className="font-bold text-[#05144b] text-sm">{pkg.name}</h4>
                                    <p className="text-[10px] font-bold text-[#05144b]">{pkg.tagline}</p>
                                </div>
                            </div>
                            <div className="mb-8 font-bold text-[#05144b]">
                                <span className="text-4xl text-[#05144b]">₹{pkg.price}</span>
                                <span className="text-[#05144b] font-bold text-xs ml-2">/ {pkg.period}</span>
                            </div>
                            <ul className="space-y-4 mb-8">
                                {pkg.categories?.[0]?.features?.map((feat: string, i: number) => (
                                    <li key={i} className="flex items-center gap-3 text-sm font-bold text-[#05144b]">
                                        <i className="fa-solid fa-circle-check text-[#05144b]"></i>
                                        {feat}
                                    </li>
                                ))}
                            </ul>
                            <button 
                                onClick={() => handlePackageInquiry(pkg._id)}
                                disabled={submittingPkgId === pkg._id}
                                className={`w-full py-4 rounded-2xl font-bold text-sm transition-all ${pkg.popular ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200' : 'bg-blue-50 dark:bg-slate-700 text-[#05144b] dark:text-[#05144b] hover:bg-blue-100'}`}
                            >
                                {submittingPkgId === pkg._id ? 'Sending Request...' : 'Send Inquiry'}
                            </button>
                            <p className="text-[9px] font-bold text-center text-[#05144b] dark:text-[#05144b] mt-4">Manual administrative activation required upon inquiry.</p>
                        </div>
                    ))}
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="mb-6 disable-animations">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-2xl font-black text-black mb-1">Add New Project</h3>
                        <p className="text-gray-500 font-semibold text-sm">Explore the 10 modular blocks to finalize your professional portfolio.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {userProfile?.assignedPackage && (
                            <div className="bg-white border border-blue-900/10 px-5 py-3 rounded-xl hidden md:block">
                                <p className="text-[11px] font-black tracking-[0.2em] text-blue-950 mb-1">Active developer plan</p>
                                <p className="text-sm font-black text-blue-900 leading-none mb-1">{userProfile.assignedPackage.name}</p>
                                <p className="text-[12px] font-bold text-blue-950">Quota: {userProfile.remainingProjects} Projects left</p>
                            </div>
                        )}
                        <Link to="/admin-projects" className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-900/10 rounded-xl text-xs font-bold text-blue-950 transition-all shadow-sm">
                            <i className="fa-solid fa-arrow-left"></i> Back to projects
                        </Link>
                        
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => generateSampleExcel()}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-950 rounded-xl text-xs font-bold transition-all border border-blue-900/5"
                                title="Download Excel Template"
                            >
                                <i className="fa-solid fa-download"></i> Template
                            </button>
                             <button 
                                onClick={() => fileInputRef.current?.click()}
                                disabled={importing}
                                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-[#05144b] text-[#05144b] rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-50"
                            >
                                <i className={importing ? "fa-solid fa-circle-notch fa-spin" : "fa-solid fa-file-import"}></i>
                                {importing ? 'Importing...' : 'Import Excel'}
                            </button>
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                className="hidden" 
                                accept=".xlsx, .xls" 
                                onChange={handleImportExcel} 
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* QUICK MEDIA FOLDER LOADER CARD */}
            <div className="bg-gradient-to-r from-blue-900/5 via-indigo-900/5 to-purple-900/5 border-2 border-indigo-900/20 rounded-2xl p-4 md:p-5 mb-8 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-[#05144b] to-[#4c1d95] text-white flex items-center justify-center text-lg shadow-md shrink-0">
                            <i className="fa-solid fa-folder-open"></i>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h4 className="text-sm font-black text-[#05144b]">Project Media Folder Auto-Import</h4>
                                <span className="bg-[#f1ae24] text-black text-[10px] font-black px-2 py-0.5 rounded-full uppercase">Instant Load</span>
                            </div>
                            <p className="text-xs text-slate-500 font-medium">Enter your Media Folder Number to automatically populate Floor Variants, Gallery, Cover & Documents</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {availableMediaFolders.length > 0 && (
                            <select
                                value={mediaFolderInput}
                                onChange={e => {
                                    setMediaFolderInput(e.target.value);
                                    if (e.target.value) handleApplyMediaFolder(e.target.value);
                                }}
                                className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#05144b]/20"
                            >
                                <option value="">Select Folder...</option>
                                {availableMediaFolders.map(f => (
                                    <option key={f.folderNumber} value={f.folderNumber}>
                                        Folder #{f.folderNumber} - {f.folderName} ({f.totalFiles || 0} files)
                                    </option>
                                ))}
                            </select>
                        )}

                        <div className="flex items-center gap-1.5">
                            <input
                                type="number"
                                min="1"
                                placeholder="Folder #"
                                value={mediaFolderInput}
                                onChange={e => setMediaFolderInput(e.target.value)}
                                className="w-24 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-[#05144b]/20"
                            />
                            <button
                                type="button"
                                onClick={() => handleApplyMediaFolder()}
                                disabled={loadingMediaFolder || !mediaFolderInput}
                                className="px-4 py-2 bg-[#05144b] hover:bg-[#4c1d95] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md shadow-indigo-950/20 disabled:opacity-50 transition-all"
                            >
                                {loadingMediaFolder ? (
                                    <i className="fa-solid fa-spinner animate-spin"></i>
                                ) : (
                                    <i className="fa-solid fa-bolt"></i>
                                )}
                                Load Assets
                            </button>
                        </div>

                        <Link
                            to="/admin-media"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-2 bg-white hover:bg-slate-100 text-indigo-700 border border-indigo-200 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all"
                            title="Open Media Manager in new tab"
                        >
                            <i className="fa-solid fa-arrow-up-right-from-square"></i> Media Manager
                        </Link>
                    </div>
                </div>

                {appliedMediaFolder && (
                    <div className="mt-3 pt-3 border-t border-indigo-100/60 flex items-center justify-between text-xs font-bold text-emerald-700 bg-emerald-50/70 p-2.5 rounded-xl border border-emerald-200">
                        <div className="flex items-center gap-2">
                            <i className="fa-solid fa-circle-check text-emerald-600 text-sm"></i>
                            <span>Connected: <b>Folder #{appliedMediaFolder.folderNumber}</b> ({appliedMediaFolder.folderName})</span>
                            <span className="text-slate-500 font-medium">| {appliedMediaFolder.floorVariants?.length || 0} Floor Variants, {Object.values(appliedMediaFolder.gallery || {}).flat().length} Gallery, {appliedMediaFolder.documents?.legalDocuments?.length || 0} Legal Docs loaded</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setAppliedMediaFolder(null);
                                setMediaFolderInput('');
                                setForm(prev => ({ ...prev, mediaFolderNumber: undefined }));
                            }}
                            className="text-rose-600 hover:underline text-[11px]"
                        >
                            Disconnect
                        </button>
                    </div>
                )}
            </div>

            {/* 10-Step Horizontal Progress Stepper */}
            <div className="bg-white border border-gray-150 rounded-2xl shadow-sm mb-8 overflow-hidden p-6">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white font-extrabold shadow-md text-base">
                            {currentStep}
                        </div>
                        <div>
                            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active Step {currentStep} of 10</span>
                            <h4 className="text-base font-extrabold text-gray-800 leading-tight">{STEPS[currentStep - 1]}</h4>
                        </div>
                    </div>
                    <div className="text-right">
                        <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Completion Progress</span>
                        <div className="flex items-center gap-2 justify-end">
                            <span className="text-lg font-black text-blue-600">{Math.round((currentStep / 10) * 100)}%</span>
                            <span className="text-gray-400 text-xs font-bold">/ 100%</span>
                        </div>
                    </div>
                </div>

                {/* Horizontal segment progress trackers */}
                <div className="flex items-center w-full mt-4 overflow-x-auto pb-4 hide-scrollbar">
                    {STEPS.map((stepName, index) => {
                        const stepId = index + 1;
                        const isSegActive = currentStep === stepId;
                        const isSegCompleted = currentStep > stepId;

                        // To match the exact image:
                        // Completed & Active are colored. Pending are gray.
                        // Active = Blue. Completed = Green.
                        let colorClass = 'text-gray-400 border-gray-200';
                        let circleClass = 'bg-gray-100 text-gray-500';
                        
                        if (isSegActive) {
                            colorClass = 'text-blue-600 border-blue-600';
                            circleClass = 'bg-blue-600 text-white shadow-md';
                        } else if (isSegCompleted) {
                            colorClass = 'text-[#1a9b55] border-[#1a9b55]'; // Exact green from image
                            circleClass = 'bg-[#1a9b55] text-white shadow-md';
                        }

                        return (
                            <div key={stepId} className="flex-1 min-w-[max-content] px-2 relative group cursor-pointer" onClick={() => { if (validateStep(currentStep) || stepId < currentStep) setCurrentStep(stepId); }}>
                                <div className={`flex items-center gap-2.5 pb-3 border-b-2 transition-all duration-300 ${colorClass}`}>
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center font-black text-xs transition-all ${circleClass}`}>
                                        {stepId}
                                    </div>
                                    <div className="text-left whitespace-nowrap">
                                        <span className="block text-[11px] font-black tracking-wide leading-tight">
                                            {stepName}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            
            {msg && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${msg.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-rose-50 border border-rose-200 text-rose-900' }`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} text-lg`}></i>
                    <span className="font-bold text-xs uppercase tracking-wider">{msg.text}</span>
                    <button onClick={() => setMsg(null)} className="ml-auto opacity-40"><i className="fa-solid fa-xmark"></i></button>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                {/* 2-Column Split-Pane Flex Layout */}
                <div className="flex flex-col lg:flex-row gap-1.5 items-start mb-20">
                    
                    {/* Left Column: Form Content */}
                    <div className="flex-1 min-w-0 space-y-6">
                        <div className="bg-white border border-gray-150 rounded-xl px-2 md:px-4 py-6 md:py-8 shadow-sm">
                            {currentStep === 1 && (
                                <Step1BasicInfo 
                                    form={form} 
                                    setForm={setForm} 
                                    handleChange={handleChange} 
                                    errors={errors}
                                    featuredImage={featuredImage}
                                    setFeaturedImage={setFeaturedImage}
                                    featuredPreview={featuredPreview}
                                    setFeaturedPreview={setFeaturedPreview}
                                />
                            )}
                            {currentStep === 2 && (
                                <Step2Location 
                                    form={form} 
                                    handleChange={handleChange} 
                                    cities={cities} 
                                    filteredLocalities={filteredLocalities} 
                                    errors={errors} 
                                />
                            )}
                            {currentStep === 3 && (
                                <Step3Developer 
                                    form={form} 
                                    setForm={setForm} 
                                    handleChange={handleChange} 
                                    developers={developers} 
                                    setDevelopers={setDevelopers}
                                    errors={errors} 
                                    showDeveloperOnly={true} 
                                />
                            )}
                            {currentStep === 4 && (
                                <Step4Overview 
                                    form={form} 
                                    setForm={setForm}
                                    handleChange={handleChange} 
                                    errors={errors} 
                                    scoreSettings={scoreSettings}
                                />
                            )}
                            {currentStep === 5 && (
                                <div className="space-y-10">
                                    <Step5SEO 
                                        form={form} 
                                        handleChange={handleChange} 
                                        errors={errors} 
                                        seoImageFile={seoImageFile}
                                        setSeoImageFile={setSeoImageFile}
                                        seoImagePreview={seoImagePreview}
                                        setSeoImagePreview={setSeoImagePreview}
                                    />
                                </div>
                            )}
                            {currentStep === 6 && (
                                <Step4FloorPlan 
                                    activeStep4Tab={activeStep4Tab}
                                    setActiveStep4Tab={setActiveStep4Tab}
                                    floorPlans={floorPlans}
                                    setFloorPlans={setFloorPlans}
                                    getImageUrl={getImageUrl}
                                    errors={errors}
                                    form={form}
                                />
                            )}
                            {currentStep === 7 && (
                                <Step8Media 
                                    featuredPreview={featuredPreview}
                                    setFeaturedImage={setFeaturedImage}
                                    setFeaturedPreview={setFeaturedPreview}
                                    tpPreview={tpPreview}
                                    setTpImage={setTpImage}
                                    setTpPreview={setTpPreview}
                                    galleryPreviews={galleryPreviews}
                                    galleryIcons={galleryIcons}
                                    handleGalleryUpload={handleGalleryUpload}
                                    updateGalleryIcon={updateGalleryIcon}
                                    removeGalleryImage={removeGalleryImage}
                                    removeGalleryCategory={removeGalleryCategory}
                                    brochureFile={brochureFile}
                                    setBrochureFile={setBrochureFile}
                                    form={form}
                                    setForm={setForm}
                                    handleChange={handleChange}
                                    errors={errors}
                                />
                            )}
                            {currentStep === 8 && (
                                <Step8Grid
                                    form={form}
                                    setForm={setForm}
                                    handleChange={handleChange}
                                    errors={errors}
                                    selectedAmenities={selectedAmenities}
                                    setSelectedAmenities={setSelectedAmenities}
                                    globalAmenities={globalAmenities}
                                    specifications={specifications}
                                    setSpecifications={setSpecifications}
                                    legalDocuments={legalDocuments}
                                    setLegalDocuments={setLegalDocuments}
                                    globalLandmarkCategories={globalLandmarkCategories}
                                    scoreSettings={scoreSettings}
                                />
                            )}
                            {currentStep === 9 && (
                                <Step9Offers 
                                    form={form} 
                                    setForm={setForm} 
                                    errors={errors} 
                                />
                            )}
                            {currentStep === 10 && (
                                <div className="space-y-10">
                                    <Step13SectionInfo 
                                        form={form} 
                                        setForm={setForm} 
                                        handleChange={handleChange}
                                    />
                                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center">
                                        <i className="fa-solid fa-rocket text-blue-500 text-5xl mb-4"></i>
                                        <h3 className="text-2xl font-black text-blue-900 mb-2">Ready to Publish</h3>
                                        <p className="text-blue-800 text-sm font-semibold mb-6">You have completed all necessary steps. Review your entries and click below to finalize.</p>
                                        <button 
                                            type="button" 
                                            onClick={(e) => handleSubmit(e, 'Pending')}
                                            disabled={submitting}
                                            className="px-8 py-4 bg-blue-600 text-white rounded-xl text-lg font-black transition-all shadow-xl shadow-blue-200 hover:bg-blue-700 active:scale-95 disabled:opacity-50 inline-flex items-center gap-3"
                                        >
                                            {submitting ? (
                                                <><i className="fa-solid fa-circle-notch animate-spin"></i> Publishing...</>
                                            ) : (
                                                <><i className="fa-solid fa-check-double"></i> Publish Final Project</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex items-center justify-between gap-4 mt-8 bg-slate-50 p-5 rounded-xl border border-slate-200 shadow-sm">
                            <button
                                type="button"
                                className="px-6 py-2.5 rounded-lg bg-white border border-gray-200 text-slate-700 font-semibold text-sm flex items-center gap-2 transition-all hover:bg-gray-50 active:scale-95 shadow-sm"
                                onClick={() => navigate('/admin-projects')}
                            >
                                <i className="fa-solid fa-arrow-left text-xs"></i>
                                Back to List
                            </button>

                            <div className="flex items-center gap-3">
                                <button 
                                    type="button" 
                                    onClick={() => handleSubmit(undefined, 'Draft')}
                                    disabled={submitting}
                                    className="px-6 py-2.5 bg-white border border-gray-200 text-slate-700 rounded-lg text-sm font-semibold transition-all hover:bg-gray-50 flex items-center gap-2 active:scale-95 disabled:opacity-50 shadow-sm"
                                >
                                    <i className="fa-solid fa-file-signature text-slate-500"></i> Save as Draft
                                </button>
                                
                                <button
                                    type="button"
                                    disabled={submitting}
                                    className="px-6 py-2.5 rounded-lg bg-[#05144b] text-white font-semibold text-sm flex items-center gap-2 transition-all hover:bg-[#0a1e6e] active:scale-95 disabled:opacity-50 shadow-sm"
                                    onClick={currentStep === 10 ? (e) => handleSubmit(e, 'Pending') : handleNextWithDraft}
                                >
                                    {submitting ? (
                                        <>
                                            <i className="fa-solid fa-circle-notch animate-spin"></i>
                                            {currentStep === 10 ? 'Publishing...' : 'Saving...'}
                                        </>
                                    ) : (
                                        <>
                                            {currentStep === 10 ? 'Publish Final Project' : `Save & Continue`}
                                            <i className="fa-solid fa-arrow-right text-xs"></i>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: 10-step Sidebar Navigation */}
                    <div className="w-full lg:w-40 shrink-0 sticky top-28 space-y-3 bg-white border border-gray-150 p-2 md:p-3 rounded-xl shadow-sm">
                        <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Workflow Steps</h5>
                        <div className="flex flex-col gap-2">
                            {STEPS.map((stepName, index) => {
                                const stepNum = index + 1;
                                const isActive = currentStep === stepNum;
                                const isCompleted = stepNum < currentStep;
                                
                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => {
                                            if (validateStep(currentStep) || stepNum < currentStep) {
                                                setCurrentStep(stepNum);
                                            }
                                        }}
                                        className={`flex items-center gap-0.5 w-full px-1 py-3 rounded-xl font-bold text-xs text-left transition-all ${
                                            isActive 
                                                ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600' 
                                                : isCompleted
                                                    ? 'text-emerald-600 hover:bg-gray-50'
                                                    : 'text-gray-500 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                                            isActive 
                                                ? 'bg-blue-600 text-white' 
                                                : isCompleted
                                                    ? 'bg-emerald-50 text-emerald-600'
                                                    : 'bg-gray-100 text-gray-400'
                                        }`}>
                                            {isCompleted ? <i className="fa-solid fa-check text-[8px]"></i> : stepNum}
                                        </span>
                                        <span className="whitespace-normal leading-tight">{stepName}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                </div>
            </form>

            {/* Premium Import Confirmation Modal */}
            {showImportConfirm && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-blue-950/40 backdrop-blur-sm">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-md overflow-hidden shadow-2xl">
                        <div className="p-8 text-center">
                            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6 text-blue-950">
                                <i className="fa-solid fa-file-excel text-3xl"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-blue-950 mb-2">Import Project Excel</h3>
                            <p className="text-blue-900/60 font-bold text-sm mb-4 leading-relaxed">
                                We found <span className="text-blue-950 font-black px-2 py-0.5 bg-blue-50 rounded-md">{pendingImportData.length}</span> project(s) in your file.
                            </p>

                            {pendingImportData[0]?.mediaFolderNumber && (
                                <div className="mb-6 p-3 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center gap-2 text-indigo-900 font-bold text-xs">
                                    <i className="fa-solid fa-folder-open text-indigo-600"></i>
                                    <span>Media Folder #{pendingImportData[0].mediaFolderNumber} will be automatically assigned!</span>
                                </div>
                            )}
                            
                            <div className="flex flex-col gap-3">
                                <button 
                                    onClick={() => loadProjectIntoForm(pendingImportData[0])}
                                    className="w-full py-4 bg-[#05144b] text-white hover:bg-[#4c1d95] rounded-2xl font-bold text-xs shadow-lg transition-all flex items-center justify-center gap-2"
                                >
                                    <i className="fa-solid fa-pen-to-square"></i>
                                    {pendingImportData.length === 1 ? 'Load into Form & Apply Media' : 'Load 1st Project into Form'}
                                </button>
                                <button 
                                    onClick={processBulkImport}
                                    className="w-full py-3.5 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-2xl font-bold text-xs shadow-sm transition-all flex items-center justify-center gap-2"
                                >
                                    <i className="fa-solid fa-cloud-arrow-up"></i>
                                    {pendingImportData.length === 1 ? 'Import directly as Draft' : `Import all ${pendingImportData.length} as Drafts`}
                                </button>
                                <button 
                                    onClick={() => { setShowImportConfirm(false); setPendingImportData([]); }}
                                    className="w-full py-3 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-2xl font-bold text-xs transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                        <div className="bg-blue-50/50 p-4 text-center">
                            <p className="text-[10px] font-black text-blue-900/40 uppercase tracking-widest">Property Guru Bulk Management System</p>
                        </div>
                    </div>
                </div>
            )}

            {/* NEARBY PLACES MODAL */}            {showNearbyModal && (
                 <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#05144b]/40 backdrop-blur-md">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-lg overflow-hidden shadow-2xl border border-[#05144b]/10">
                        <div className="px-8 py-6 bg-white border-b-2 border-[#05144b] text-[#05144b] flex items-center justify-between">
                            <h3 className="text-xl font-bold">Add nearby place</h3>
                            <button onClick={() => setShowNearbyModal(false)} className="text-[#05144b] transition-all"><i className="fa-solid fa-xmark text-xl"></i></button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[12px] font-bold text-black ml-1">Select category</label>
                                <select 
                                    className="w-full px-5 py-4 rounded-xl bg-white border border-blue-900/10 text-black font-bold text-sm outline-none focus:border-[#05144b] appearance-none"
                                    value={nearbyEntry.category}
                                    onChange={(e) => setNearbyEntry({ ...nearbyEntry, category: e.target.value })}
                                >
                                    {(globalLandmarkCategories.length > 0 
                                        ? globalLandmarkCategories.map(c => c.name) 
                                        : Object.keys(NEARBY_CATEGORY_ICONS)
                                    ).map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[12px] font-bold text-black ml-1">Place name</label>
                                <input 
                                    type="text" 
                                    className="w-full px-5 py-4 rounded-xl bg-white border border-blue-900/10 text-black font-bold text-sm outline-none focus:border-[#05144b]"
                                    placeholder="Enter school or hospital name..."
                                    value={nearbyEntry.name}
                                    onChange={(e) => setNearbyEntry({ ...nearbyEntry, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[12px] font-bold text-black ml-1">Distance (in KM)</label>
                                <div className="relative">
                                    <input 
                                        type="number" 
                                        step="0.1"
                                        className="w-full px-5 py-4 rounded-xl bg-white border border-blue-900/10 text-black font-bold text-sm outline-none focus:border-[#05144b]"
                                        placeholder="0.5"
                                        value={nearbyEntry.distance}
                                        onChange={(e) => setNearbyEntry({ ...nearbyEntry, distance: e.target.value })}
                                    />
                                    <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[10px] font-black text-black opacity-40">KM</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <button 
                                    type="button"
                                    className="w-full py-5 bg-white border border-blue-900/10 text-black rounded-2xl font-bold text-xs active:scale-95 transition-all shadow-sm"
                                    onClick={() => setShowNearbyModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button"
                                    className="w-full py-5 bg-white border-2 border-[#05144b] text-[#05144b] rounded-2xl font-bold text-xs ] active:scale-95 transition-all shadow-xl"
                                    onClick={() => {
                                        if(!nearbyEntry.name || !nearbyEntry.distance) {
                                            toast.error("Please fill all fields");
                                            return;
                                        }
                                        setForm({ ...form, nearbyPlaces: [...form.nearbyPlaces, nearbyEntry] });
                                        setNearbyEntry({ category: 'Schools', name: '', distance: '' });
                                        setShowNearbyModal(false);
                                    }}
                                >
                                    Save place
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}



